import Service from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';

export default class GraphQLPerformanceService extends Service {
  @service('graphql-optimizer') graphqlOptimizer;
  @service('network-retry') networkRetry;

  @tracked queryBatches = new Map();
  @tracked queryMetrics = new Map();
  @tracked batchTimer = null;

  // Performance thresholds
  BATCH_WINDOW_MS = 100; // Batch queries within 100ms window
  MAX_QUERY_COMPLEXITY = 1000; // Maximum allowed query complexity
  SLOW_QUERY_THRESHOLD = 2000; // Queries > 2s are considered slow
  MAX_BATCH_SIZE = 10; // Maximum queries per batch

  /**
   * Analyzes GraphQL query complexity to prevent expensive operations
   * @param {string} query - GraphQL query string
   * @param {Object} variables - Query variables
   * @returns {Object} Complexity analysis result
   */
  analyzeQueryComplexity(query, variables = {}) {
    const complexity = this.calculateQueryComplexity(query, variables);
    const analysis = {
      complexity,
      isAllowed: complexity <= this.MAX_QUERY_COMPLEXITY,
      estimatedCost: this.estimateQueryCost(query, variables),
      suggestions: [],
    };

    // Add optimization suggestions for high-complexity queries
    if (complexity > this.MAX_QUERY_COMPLEXITY * 0.8) {
      analysis.suggestions = this.generateOptimizationSuggestions(query);
    }

    return analysis;
  }

  /**
   * Calculates query complexity score based on field depth and collection sizes
   * @param {string} query - GraphQL query string
   * @param {Object} variables - Query variables
   * @returns {number} Complexity score
   */
  calculateQueryComplexity(query, variables) {
    let complexity = 0;

    // Basic complexity calculation based on query patterns
    const fieldMatches = query.match(/\w+\s*\{/g) || [];
    complexity += fieldMatches.length * 10;

    // Collection queries are more expensive
    const collectionMatches = query.match(/Collection\s*\(/g) || [];
    complexity += collectionMatches.length * 50;

    // Nested queries increase complexity exponentially
    const nestedDepth = this.calculateNestingDepth(query);
    complexity += Math.pow(nestedDepth, 2) * 20;

    // Variable-based limits affect complexity
    if (variables.limit && variables.limit > 50) {
      complexity += (variables.limit - 50) * 2;
    }

    return complexity;
  }

  /**
   * Calculates maximum nesting depth of a GraphQL query
   * @param {string} query - GraphQL query string
   * @returns {number} Maximum nesting depth
   */
  calculateNestingDepth(query) {
    let maxDepth = 0;
    let currentDepth = 0;

    for (const char of query) {
      if (char === '{') {
        currentDepth++;
        maxDepth = Math.max(maxDepth, currentDepth);
      } else if (char === '}') {
        currentDepth--;
      }
    }

    return maxDepth;
  }

  /**
   * Estimates query execution cost in milliseconds
   * @param {string} query - GraphQL query string
   * @param {Object} variables - Query variables
   * @returns {number} Estimated execution time in ms
   */
  estimateQueryCost(query, variables) {
    const complexity = this.calculateQueryComplexity(query, variables);

    // Base cost estimation
    let estimatedMs = 100; // Base query cost

    // Add cost based on complexity
    estimatedMs += complexity * 2;

    // Collection queries have higher base cost
    const collectionCount = (query.match(/Collection\s*\(/g) || []).length;
    estimatedMs += collectionCount * 200;

    // Large limits increase cost significantly
    if (variables.limit && variables.limit > 20) {
      estimatedMs += variables.limit * 10;
    }

    return estimatedMs;
  }

  /**
   * Generates optimization suggestions for high-complexity queries
   * @param {string} query - GraphQL query string
   * @returns {Array} Array of optimization suggestions
   */
  generateOptimizationSuggestions(query) {
    const suggestions = [];

    if (query.includes('body {')) {
      suggestions.push('Consider removing body content from list queries');
    }

    if (query.includes('limit:') && query.includes('100')) {
      suggestions.push('Reduce query limit or implement pagination');
    }

    if (this.calculateNestingDepth(query) > 5) {
      suggestions.push(
        'Consider flattening deeply nested queries using fragments',
      );
    }

    const collectionCount = (query.match(/Collection\s*\(/g) || []).length;
    if (collectionCount > 3) {
      suggestions.push(
        'Consider splitting multiple collection queries into separate requests',
      );
    }

    return suggestions;
  }

  /**
   * Batches multiple GraphQL queries for efficient execution
   * @param {Object} queryConfig - Query configuration
   * @param {Object} context - Execution context
   * @returns {Promise<any>} Batched query result
   */
  async batchQuery(queryConfig, context) {
    const batchId = this.getCurrentBatchId();

    return new Promise((resolve, reject) => {
      // Add query to current batch
      if (!this.queryBatches.has(batchId)) {
        this.queryBatches.set(batchId, {
          queries: [],
          callbacks: [],
          context,
          createdAt: Date.now(),
        });
      }

      const batch = this.queryBatches.get(batchId);
      batch.queries.push(queryConfig);
      batch.callbacks.push({ resolve, reject });

      // Ensure batch doesn't exceed maximum size
      if (batch.queries.length >= this.MAX_BATCH_SIZE) {
        this.executeBatch(batchId);
      } else {
        // Schedule batch execution if not already scheduled
        this.scheduleBatchExecution(batchId);
      }
    });
  }

  /**
   * Gets the current batch ID based on time window
   * @returns {string} Batch identifier
   */
  getCurrentBatchId() {
    const now = Date.now();
    const batchWindow = Math.floor(now / this.BATCH_WINDOW_MS);
    return `batch-${batchWindow}`;
  }

  /**
   * Schedules batch execution after the batch window
   * @param {string} batchId - Batch identifier
   */
  scheduleBatchExecution(batchId) {
    if (!this.batchTimer) {
      this.batchTimer = setTimeout(() => {
        this.executeBatch(batchId);
        this.batchTimer = null;
      }, this.BATCH_WINDOW_MS);
    }
  }

  /**
   * Executes a batch of GraphQL queries
   * @param {string} batchId - Batch identifier
   */
  async executeBatch(batchId) {
    const batch = this.queryBatches.get(batchId);
    if (!batch) return;

    this.queryBatches.delete(batchId);

    const startTime = Date.now();

    try {
      // Execute queries in parallel for better performance
      const results = await Promise.allSettled(
        batch.queries.map((queryConfig) =>
          this.executeSingleQuery(queryConfig, batch.context),
        ),
      );

      // Resolve individual query promises
      results.forEach((result, index) => {
        const callback = batch.callbacks[index];
        if (result.status === 'fulfilled') {
          callback.resolve(result.value);
        } else {
          callback.reject(result.reason);
        }
      });

      // Record batch metrics
      this.recordBatchMetrics(
        batchId,
        batch.queries.length,
        Date.now() - startTime,
      );
    } catch (error) {
      // Reject all queries in batch if batch execution fails
      batch.callbacks.forEach((callback) => callback.reject(error));
    }
  }

  /**
   * Executes a single GraphQL query with performance monitoring
   * @param {Object} queryConfig - Query configuration
   * @param {Object} context - Execution context
   * @returns {Promise<any>} Query result
   */
  async executeSingleQuery(queryConfig, context) {
    const { query, variables = {}, cacheKey } = queryConfig;
    const startTime = Date.now();

    // Analyze query complexity before execution
    const complexityAnalysis = this.analyzeQueryComplexity(query, variables);
    if (!complexityAnalysis.isAllowed) {
      throw new Error(
        `Query complexity ${complexityAnalysis.complexity} exceeds limit ${this.MAX_QUERY_COMPLEXITY}`,
      );
    }

    try {
      // Execute query with the existing optimizer
      const result = await this.graphqlOptimizer.executeOptimizedQuery(
        async () => {
          if (context.query?.promise) {
            await context.query.promise;
            return context.query.data;
          } else {
            throw new Error('Query execution context not available');
          }
        },
        queryConfig,
        context,
      );

      // Record performance metrics
      const executionTime = Date.now() - startTime;
      this.recordQueryMetrics(
        cacheKey || query,
        executionTime,
        complexityAnalysis.complexity,
      );

      return result;
    } catch (error) {
      // Record failed query metrics
      const executionTime = Date.now() - startTime;
      this.recordQueryMetrics(
        cacheKey || query,
        executionTime,
        complexityAnalysis.complexity,
        error,
      );
      throw error;
    }
  }

  /**
   * Records performance metrics for a query
   * @param {string} queryId - Query identifier
   * @param {number} executionTime - Execution time in ms
   * @param {number} complexity - Query complexity score
   * @param {Error} error - Error if query failed
   */
  recordQueryMetrics(queryId, executionTime, complexity, error = null) {
    const existing = this.queryMetrics.get(queryId) || {
      totalExecutions: 0,
      totalTime: 0,
      averageTime: 0,
      maxTime: 0,
      minTime: Infinity,
      errorCount: 0,
      complexity,
      isSlowQuery: false,
    };

    existing.totalExecutions++;
    existing.totalTime += executionTime;
    existing.averageTime = existing.totalTime / existing.totalExecutions;
    existing.maxTime = Math.max(existing.maxTime, executionTime);
    existing.minTime = Math.min(existing.minTime, executionTime);

    if (error) {
      existing.errorCount++;
    }

    existing.isSlowQuery = existing.averageTime > this.SLOW_QUERY_THRESHOLD;

    this.queryMetrics.set(queryId, existing);

    // Log slow queries for optimization
    if (executionTime > this.SLOW_QUERY_THRESHOLD) {
      console.warn(`Slow GraphQL query detected: ${executionTime}ms`, {
        queryId: queryId.substring(0, 100),
        complexity,
        executionTime,
      });
    }
  }

  /**
   * Records batch execution metrics
   * @param {string} batchId - Batch identifier
   * @param {number} queryCount - Number of queries in batch
   * @param {number} totalTime - Total execution time
   */
  recordBatchMetrics(batchId, queryCount, totalTime) {
    console.debug(
      `GraphQL batch executed: ${queryCount} queries in ${totalTime}ms`,
      {
        batchId,
        queryCount,
        totalTime,
        averagePerQuery: Math.round(totalTime / queryCount),
      },
    );
  }

  /**
   * Gets performance metrics for monitoring
   * @returns {Object} Performance statistics
   */
  getPerformanceMetrics() {
    const metrics = Array.from(this.queryMetrics.entries()).map(
      ([queryId, stats]) => ({
        queryId: queryId.substring(0, 100), // Truncate for display
        ...stats,
      }),
    );

    const slowQueries = metrics.filter((m) => m.isSlowQuery);
    const errorQueries = metrics.filter((m) => m.errorCount > 0);

    return {
      totalQueries: metrics.length,
      slowQueries: slowQueries.length,
      errorQueries: errorQueries.length,
      averageComplexity:
        metrics.reduce((sum, m) => sum + m.complexity, 0) / metrics.length || 0,
      topSlowQueries: slowQueries
        .sort((a, b) => b.averageTime - a.averageTime)
        .slice(0, 10),
      topErrorQueries: errorQueries
        .sort((a, b) => b.errorCount - a.errorCount)
        .slice(0, 10),
    };
  }

  /**
   * Optimizes query execution based on historical performance
   * @param {Object} queryConfig - Query configuration
   * @param {Object} context - Execution context
   * @returns {Promise<any>} Optimized query result
   */
  async executeOptimizedQuery(queryConfig, context) {
    const { query, variables = {} } = queryConfig;

    // Check if this query should be batched
    const shouldBatch = this.shouldBatchQuery(query);

    if (shouldBatch) {
      return this.batchQuery(queryConfig, context);
    } else {
      return this.executeSingleQuery(queryConfig, context);
    }
  }

  /**
   * Determines if a query should be batched based on its characteristics
   * @param {string} query - GraphQL query string
   * @returns {boolean} Whether query should be batched
   */
  shouldBatchQuery(query) {
    // Batch simple queries but not complex ones
    const complexity = this.calculateQueryComplexity(query);

    // Don't batch very simple queries (< 50 complexity) or very complex ones (> 500)
    return complexity >= 50 && complexity <= 500;
  }

  /**
   * Clears performance metrics (useful for testing)
   */
  clearMetrics() {
    this.queryMetrics.clear();
    this.queryBatches.clear();
  }
}
